import { Grid, Box, Typography, colors, Button } from "@mui/material";
import { useId } from "react";
import DateTime, { Date } from "./DateTime";
import DropDown from "./DropDown";
import SwitchInput from "./SwitchInput";
import RadioOption from "./RadioOption";
import TextBox from "./TextBox";
import { DownArrowHead, UpArrowHead } from "../icons/ButtonIcons";
import moment from "moment";

export default function Form({
  sections,
  spacing = 1,
  data,
  dispatch,
  setData,
  readOnly,
  errors,
  masterData,
  onChange,
  sx,
  actions = {},
}) {
  const id = useId();

  const handleDateUp = (name, val) => {
    let newDate = moment(val).add(1, 'days');
    onDateTimeChange(newDate, name);
  }

  const handleDateDown = (name, val) => {
    let newDate = moment(val).subtract(1, 'days');
    onDateTimeChange(newDate, name);
  }

  const handleKeyUp = (event, handleEnter, name, val) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleEnter && handleEnter(event);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      handleDateUp(name, val);
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      handleDateDown(name, val);
    }
  }

  function setValue(name, value) {
    dispatch({
      type: FormActions.INPUT_CHANGED,
      payload: { name, value },
    });
  }

  function onDateTimeChange(val, name) {
    dispatch({
      type: FormActions.INPUT_CHANGED,
      payload: { name, value: val },
    });
  }

  function onChange(e, field) {
    let val = e.target?.type === "checkbox" ? e.target.checked : e.target.value;

    const num = Number(e.target.value);

    if (e.target?.type === "number" && field?.min0 !== true) {
      if (e.target.value === "") {
        e.target.value = "";
        val = "";
      } else if (num <= field.min || num <= 0) {
        e.target.value = field.min || 1;
        val = field.min || 1;
      } else if (num > field.max) {
        e.target.value = field.max;
        val = field.max;
      }
    }
    if (e.target?.type === "number" && field?.min0 === true) {
      if (e.target.value === "") {
        e.target.value = "";
        val = "";
      } else if (num <= field.min || num <= 0) {
        e.target.value = field.min || 0;
        val = field.min || 0;
      } else if (num > field.max) {
        e.target.value = field.max;
        val = field.max;
      }
    }

    // NEW: Restrict to decimal place
    if ((e.target?.type === "number" || e.target?.type === "text") && field?.decimalFix >= 0) {
      let value = e.target.value;

      if (value !== "") {
        // allow only digits and one decimal point
        value = value.replace(/[^0-9.]/g, "");

        const parts = value.split(".");

        if (parts.length > 2) {
          // if more than one decimal, keep only the first
          value = parts[0] + "." + parts.slice(1).join("").slice(0, field.decimalFix);
        } else if (parts.length === 2) {
          // keep decimal part with max allowed length
          parts[1] = parts[1].slice(0, field.decimalFix);
          value = parts[0] + "." + parts[1];
        }

        // if user typed only ".", keep it
        if (value === ".") {
          value = "0.";
        }

        // if decimalFix = 0, cut off decimals entirely
        if (field.decimalFix === 0) {
          value = parts[0];
        }

        e.target.value = value;
      }

      val = e.target.value;
    }

    if (dispatch) {
      dispatch({
        type: FormActions.INPUT_CHANGED,
        payload: { name: e.target.name, value: val },
      });
      actions.onChange && actions.onChange(e.target.name, val);
      return;
    }
    setData && setData({ ...data, [e.target.name]: val });
  }

  return (
    <Grid container spacing={spacing}>
      {sections?.map((x, i) => {
        return (
          <Grid
            item
            xs={x.xsCol || 12}
            key={i}
            container
            spacing={0.5}
            alignContent="flex-start"
            sx={sx || {}}
          >
            {x?.fields?.map((field, index) => {
              if (field !== false)
                return renderField({
                  handleKeyUp, handleDateDown, handleDateUp,
                  field,
                  index,
                  id,
                  data,
                  onChange,
                  setValue,
                  onDateTimeChange,
                  readOnly,
                  errors,
                  masterData,
                  dispatch,
                  actions,
                  sx,
                });
            })}
          </Grid>
        );
      })}
    </Grid>
  );
}

function renderField({
  handleKeyUp, handleDateDown, handleDateUp,
  field,
  index: j,
  id,
  data,
  onChange,
  setValue,
  onDateTimeChange,
  readOnly,
  errors,
  masterData,
  dispatch,
  actions,
  sx,
}) {
  if (field?.component) {
    return (
      <GridFormItem {...field} key={j}>
        {field.component({
          id,
          value: (data || {})[field.name],
          setValue: (value, name = "") => setValue(name || field.name, value),
          errorMsg: (errors || {})[field.errorId || field.name],
          onChange: onChange,
          data,
          dispatch,
          masterData,
          actions,
        })}
      </GridFormItem>
    );
  }
  if (field?.group) {
    return (
      <GridFormItem
        {...field}
        key={j}
        container
        spacing={2}
        alignContent="flex-start"
      >
        {/* <Grid >
        </Grid> */}
        {field?.group?.map((f, k) => {
          if (f !== false)
            return renderField({
              handleKeyUp, handleDateDown, handleDateUp,
              field: f,
              index: k,
              id,
              data,
              onChange,
              setValue: setValue,
              onDateTimeChange,
              readOnly,
              errors,
              masterData,
              dispatch,
              actions,
              sx,
            });
        })}
      </GridFormItem>
    );
  }
  if (
    !field?.type ||
    field?.type === "text" ||
    field?.type === "textarea" ||
    field?.type === "number"
  ) {
    const options =
      field?.type === "textarea"
        ? { multiline: true, rows: field?.rows || 3, tabIndex: field?.tabIndex || "0" }
        : (field?.type === "number")
          ? { type: "number", inputProps: { min: field?.min, max: field?.max, tabIndex: field?.tabIndex || "0" } }
          : field?.type === "text"
            ? { inputProps: { maxLength: field?.maxLength, tabIndex: field?.tabIndex || "0" } }
            : {};
    return (
      <GridFormItem {...field} key={j} hidden={getHiddenValue(data, field)}>
        <TextBox
          tabIndex={field?.tabIndex}
          autoComplete={field?.autoComplete}
          sx={field.sx}
          id={`${id}${field.name}`}
          name={field.name}
          label={field.label}
          labelColor={field.labelColor || ""}
          label2={field.label2 || ""}
          label2Color={field.label2Color || ""}
          label2bgColor={field.label2bgColor || ""}
          defaultValue={getFieldValue(data, field)}
          errorMsg={(errors || {})[field.name] || ""}
          readOnly={readOnly || field.readOnly}
          disabled={getDisabledValue(data, field)}
          onChangeAsync={(e) => onChange(e, field)}
          handleEnterPress={field?.handleEnterPress}
          {...{ ...options }}
        />
      </GridFormItem>
    );
  }
  if (field?.type === "switch") {
    return (
      <GridFormItem
        {...field}
        key={j}
        alignSelf="end"
        hidden={getHiddenValue(data, field)}
      >
        <SwitchInput
          id={`${id}${field.name}`}
          name={field.name}
          className={field.className}
          label={field.label}
          labelColor={field.labelColor || ""}
          value={(data || {})[field.name] || false}
          errorMsg={(errors || {})[field.name] || ""}
          onChange={onChange}
          disabled={getDisabledValue(data, field)}
          readOnly={getReadOnlyValue(data, field)}
        />
      </GridFormItem>
    );
  }
  if (field?.type === "button") {
    return (
      <GridFormItem
        {...field}
        key={j}
        alignSelf="end"
        hidden={getHiddenValue(data, field)}
      >
        <Button
          color={field.color}
          onClik={onChange}
          disabled={field?.disabled}
          hidden={field.hidden}
        >{field.name}
        </Button>
      </GridFormItem>
    );
  }
  if (field?.type === "radio") {
    return (
      <GridFormItem {...field} key={j} hidden={getHiddenValue(data, field)}>
        <RadioOption
          id={`${id}${field.name}`}
          name={field.name}
          label={field.label}
          value={(data || {})[field.name] || field.defaultVal || ""}
          errorMsg={(errors || {})[field.name] || ""}
          onChange={onChange}
        />

      </GridFormItem>
    );
  }
  if (field?.type === "dropdown") {
    let selectOptions = [];
    if (field?.selectOptions) {
      selectOptions = field?.selectOptions;
    } else if (field?.optionConfigs) {
      selectOptions = ((masterData && masterData[field?.optionConfigs?.name]) || [])?.map(
        (x) => ({
          value: x[field?.optionConfigs?.valProp || "value"],
          text: x[field?.optionConfigs?.textProp || "text"],
        })
      );
    } else if (field?.optionsArray) {
      selectOptions = field?.optionsArray?.map((x, i) => ({
        value: i,
        text: x,
      }));
    } else if (field?.optionsObject) {
      selectOptions = Object.keys(field?.optionsObject)?.map((key) => ({
        value: key,
        text: field.optionsObject[key],
      }));
    }

    return (
      <GridFormItem
        {...field}
        //alignSelf={!field.label ? "flex-end" : undefined}
        key={j}
        hidden={getHiddenValue(data, field)}
      >
        <DropDown
          // sx={{ minWidth: "230px" }} disturbing when moves to smaller screen
          id={`${id}${field.name}`}
          name={field.name}
          label={field.label}
          labelColor={field.labelColor || ""}
          label2={field.label2 || ""}
          label2Color={field.label2Color || ""}
          label2bgColor={field.label2bgColor || ""}
          value={(data || {})[field.name] || 0}
          errorMsg={(errors || {})[field.name] || ""}
          onChange={field.onChange || onChange}
          selectOptions={selectOptions}
          required={field.required}
          disabled={getDisabledValue(data, field)}
          readOnly={getReadOnlyValue(data, field)}
        />
      </GridFormItem>
    );
  }
  if (field?.type === "datetime") {
    return (
      <GridFormItem {...field} key={j} hidden={getHiddenValue(data, field)}>
        <DateTime
          id={`${id}${field.name}`}
          name={field.name}
          label={field.label}
          value={(data || {})[field.name] || ""}
          errorMsg={(errors || {})[field.name] || ""}
          onChange={onDateTimeChange}
          disabled={getDisabledValue(data, field)}
          disablePast={field.disablePast}
          disableFuture={field.disableFuture}
          readOnly={readOnly || field.readOnly}
          maxDate={field?.maxDate}
          minDate={field?.minDate}
        />
      </GridFormItem>
    );
  }
  if (field?.type === "date") {
    return (
      <GridFormItem
        sx={{ maxWidth: "200px" }}
        {...field}
        key={j}
        hidden={getHiddenValue(data, field)}
      >
        <Grid container spacing={0}>
          <Grid item xs={field?.arrowCheck === true ? 11 : 12} >
            <Date
              id={`${id}${field.name}`}
              name={field.name}
              label={field.label}
              value={(data || {})[field.name] || ""}
              errorMsg={(errors || {})[field.name] || ""}
              onChange={onDateTimeChange}
              disabled={getDisabledValue(data, field)}
              readOnly={readOnly || field.readOnly}
              disablePast={field.disablePast}
              disableFuture={field.disableFuture}
              maxDate={field?.maxDate}
              minDate={field?.minDate}
              handleEnterPress={field?.handleEnterPress}
              handleKeyUp={handleKeyUp}
            />
          </Grid>
          {field?.arrowCheck === true ? <Grid item xs={1} alignContent={field.label === undefined ? "" : "center"}  >
            <div title="Date Up"
              onClick={() => handleDateUp(field.name, (data || {})[field.name])}
              style={{ height: "5px", cursor: "pointer" }}>
              {/* {"⌃"} */}
              <UpArrowHead />
            </div>

            <div title="Date Down"
              onClick={() => handleDateDown(field.name, (data || {})[field.name])}
              style={{ marginTop: "7px", height: "5px", cursor: "pointer" }}>
              {/* {"⌄"} */}
              <DownArrowHead />
            </div>
          </Grid> : <></>}
        </Grid>

      </GridFormItem>
    );
  }
  // if (field.type === "label") {
  //   return (
  //     <GridFormItem {...field}  key={j}>
  //       <FormHelperTextLabel label={field.label} />
  //     </Grid>
  //   );
  // }
}

function GridFormItem({
  xsCol,
  children,
  container,
  direction,
  spacing,
  alignContent,
  alignItems,
  alignSelf,
  flex,
  sx,
  hidden,
}) {
  const options = {
    container: container,
    spacing: spacing,
    alignSelf: alignSelf,
    alignContent: alignContent,
    alignItems: alignItems,
    direction: direction,
    flex: flex,
    sx: sx,
    hidden,
  };
  if (xsCol) {
    options.xs = xsCol;
  }
  return (
    <Grid item {...options}>
      {children}
    </Grid>
  );
}

function getFieldValue(data, field) {
  if (typeof field.getValue === "function") {
    return field.getValue(data);
  }
  const val = (data || {})[field.name];
  return val || val === 0 ? val : "";
}

function getDisabledValue(data, field) {
  return (
    field.disabled &&
    (typeof field.disabled === "function"
      ? field.disabled(data)
      : field.disabled)
  );
}
function getReadOnlyValue(data, field) {
  return (
    field.readOnly &&
    (typeof field.readOnly === "function"
      ? field.readOnly(data)
      : field.readOnly)
  );
}

function getHiddenValue(data, field) {
  return (
    field.hidden &&
    (typeof field.hidden === "function" ? field.hidden(data) : field.hidden)
  );
}

export function SectionTitle({ title }) {
  return (
    <Box
      sx={{ backgroundColor: "primary.main", color: colors.common.white }}
      p={1}
    >
      <Typography>{title}</Typography>
    </Box>
  );
}

export const FormActions = {
  LOAD_DATA: "load-data",
  INPUT_CHANGED: "input-changed",
  RESET_FORM: "reset-form",
};
